import { HttpClient, robotoHeaders, PaginatedResponse } from "../../http";

import {
  ChatContent,
  ChatMessage,
  ChatRecord,
  ChatRecordDelta,
  ChatRole,
  ChatTextContent,
  ChatToolDetailResponse,
  isChatTextContent,
  RobotoLLMContext,
  SendMessageRequest,
  StartChatRequest,
} from "./ChatRecord";

interface Options {
  abortSignal: AbortSignal;
  orgId: string;
}

/**
 * Service for interacting with chat-related API endpoints.
 *
 * Provides methods for starting new chat sessions and retrieving chat records.
 */
export class ChatService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Start a new chat session with an initial message.
   *
   * @param message - The initial message text to start the chat
   * @param options - Optional configuration including orgId and abort signal
   * @returns Promise resolving to the created ChatRecord
   * @throws Error if orgId is not provided
   */
  public async startChat(
    message: string,
    options?: Partial<
      {
        context: RobotoLLMContext;
      } & Options
    >,
  ): Promise<ChatRecord> {
    if (!options?.orgId) {
      throw Error("startChat requires an Org ID, none was provided");
    }

    const chatMessage: ChatMessage = {
      role: ChatRole.USER,
      content: [{ text: message } as ChatTextContent],
    };

    const request: StartChatRequest = {
      messages: [chatMessage],
      context: options?.context,
    };

    const requestUrl = this.#httpClient.constructUrl("v1/ai/chats");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });

    return await response.json<ChatRecord>();
  }

  /**
   * Get a chat record by its ID.
   *
   * @param chatId - The unique identifier of the chat session
   * @param loadMessages - Whether to load the full message history (default: true)
   * @param options - Optional configuration including abort signal
   * @returns Promise resolving to the ChatRecord
   */
  public async getChatById(
    chatId: string,
    loadMessages: boolean = true,
    options?: Partial<Options>,
  ): Promise<ChatRecord> {
    const searchParams = new URLSearchParams();
    searchParams.append("load_messages", loadMessages.toString());

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/chats/${chatId}`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<ChatRecord>();
  }

  public async getToolDetails(
    chatId: string,
    toolUseId: string,
    options?: Partial<Options>,
  ): Promise<ChatToolDetailResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/chats/${chatId}/tool-use/${toolUseId}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<ChatToolDetailResponse>();
  }

  /**
   * Combines consecutive text content blocks in a content array.
   * This prevents multiple text chunks from appearing as separate message bubbles.
   */
  private combineConsecutiveTextContent(content: ChatContent[]): ChatContent[] {
    const combined: ChatContent[] = [];

    for (const item of content) {
      if (isChatTextContent(item)) {
        const lastItem = combined[combined.length - 1];
        if (lastItem && isChatTextContent(lastItem)) {
          // Merge with previous text content
          lastItem.text += item.text;
        } else {
          // Add new text content
          combined.push({ text: item.text });
        }
      } else {
        // Non-text content, add as-is
        combined.push(item);
      }
    }

    return combined;
  }

  public async updateChatFromDeltas(
    record: ChatRecord,
    options?: Partial<Options>,
  ): Promise<ChatRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/chats/${record.chat_id}/delta`,
      new URLSearchParams({ next_token: record.continuation_token }),
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    const delta = await response.json<ChatRecordDelta>();

    // Create a new messages array by copying existing messages
    const updatedMessages = record.messages.map((msg) => ({
      ...msg,
      content: [...msg.content],
    }));

    // Get sorted indices from messages_by_idx
    const sortedIndices = Object.keys(delta.messages_by_idx)
      .map((idx) => parseInt(idx, 10))
      .sort((a, b) => a - b);

    // Process each message in sorted order
    for (const idx of sortedIndices) {
      const deltaMessage = delta.messages_by_idx[idx];

      if (idx < updatedMessages.length) {
        // Update existing message - combine content and merge consecutive text blocks
        const combinedContent = this.combineConsecutiveTextContent([
          ...updatedMessages[idx].content,
          ...deltaMessage.content,
        ]);

        updatedMessages[idx] = {
          ...updatedMessages[idx],
          status: deltaMessage.status,
          content: combinedContent,
        };
      } else {
        // Append new message
        updatedMessages.push(deltaMessage);
      }
    }

    // Return a new ChatRecord object with updated fields
    return {
      ...record,
      continuation_token: delta.continuation_token,
      status:
        delta.status !== undefined && delta.status !== null
          ? delta.status
          : record.status,
      title:
        delta.title !== undefined && delta.title !== null
          ? delta.title
          : record.title,
      messages: updatedMessages,
    };
  }

  /**
   * Send a new message to an existing chat conversation.
   *
   * This will add the user's message to the chat and trigger the assistant
   * to generate a response.
   *
   * @param chatId - The unique identifier of the chat session
   * @param message - The message text to send
   * @param options - Optional configuration including orgId and abort signal
   * @returns Promise that resolves when the message is sent
   * @throws Error if orgId is not provided
   */
  public async sendMessage(
    chatId: string,
    message: string,
    options?: Partial<
      {
        context: RobotoLLMContext;
      } & Options
    >,
  ): Promise<void> {
    if (!options?.orgId) {
      throw Error("sendMessage requires an Org ID, none was provided");
    }

    const chatMessage: ChatMessage = {
      role: ChatRole.USER,
      content: [{ text: message } as ChatTextContent],
    };

    const request: SendMessageRequest = {
      message: chatMessage,
      context: options?.context,
    };

    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/chats/${chatId}/messages`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify(request),
    });
  }

  /**
   * Get a paginated list of chats for the current user.
   *
   * @param options - Optional configuration including pageToken and abort signal
   * @returns Promise resolving to a paginated response of ChatRecords
   */
  public async getChatsForUser(
    options?: Partial<{
      pageToken: string;
      abortSignal: AbortSignal;
    }>,
  ): Promise<PaginatedResponse<ChatRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/ai/chats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<PaginatedResponse<ChatRecord>>();
  }

  /**
   * Request cancellation of the current generation for a chat.
   *
   * This signals to the backend worker that the user wants to stop the current
   * generation. The worker will gracefully terminate and mark the message as cancelled.
   *
   * @param chatId - The unique identifier of the chat session
   * @param options - Optional configuration including abort signal
   * @returns Promise that resolves when the cancellation request is sent
   */
  public async cancelGeneration(
    chatId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/ai/chats/${chatId}/cancel`,
    );

    await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
    });
  }
}
